// navigation-completion Stage C/D — frontend API client for the navigation
// RELEASE status plus the two INDEPENDENT approval boundaries (pre-upload intake
// evidence and pre-release publishing QC).
//
// A SEPARATE module from v2Client.ts (not edited here); it reuses v2Client's
// exported `apiFetch` so auth, base URL and errors are shared. The wire types
// mirror src/routes/navigation-release.ts EXACTLY. The UI reads the server
// verdict and NEVER re-derives release eligibility or invents an artifact hash /
// compiler package id:
//   • Intake binds to the SELECTED DOCUMENT's SOURCE bytes (pre-upload approval);
//     the client confirms the bytes it saw (expectedArtifactSha256 = source hash)
//     and the server supplies the authoritative hash and refuses a mismatch.
//   • Publishing QC binds to the sealed FINAL package bytes AND the exact compiler
//     package id the reviewer inspected; both are echoed and the server refuses a
//     superseded compile (409) rather than silently signing the latest one.
//

import { apiFetch } from '@/api/v2Client';

export type RegulatoryConformance = 'pass' | 'fail' | 'unknown';
export type ReleaseStatus = 'none' | 'release-eligible' | 'blocked';

export interface ReleaseBlockerWire {
  readonly code: string;
  readonly detail: string;
}

export type QcGate = 'intake' | 'publishing-qc';
export type QcKind = 'attestation' | 'verified-record' | 'qc-signoff';

export interface QcRecordWire {
  readonly id: string;
  readonly gate: QcGate;
  readonly kind: QcKind;
  readonly actor: string;
  readonly boundArtifactSha256: string;
  readonly reason: string | null;
  readonly evidenceRef: string | null;
  readonly signedArtifactSha256: string | null;
  readonly createdAt: string | null;
}

/** The read-only release status. `status: 'none'` means no navigation release
 *  evidence exists yet (validate-only / legacy / not compiled with navigation) —
 *  NOT a block on its own. A blocked verdict carries its blockers verbatim. */
export interface NavigationReleaseStatus {
  readonly ok: true;
  readonly status: ReleaseStatus;
  readonly releaseEligible: boolean;
  readonly regulatoryConformance: RegulatoryConformance;
  readonly stale: boolean;
  readonly staleReason?: string | null;
  readonly blockers?: readonly ReleaseBlockerWire[];
  readonly readinessReason?: string | null;
  readonly draftInspectable?: boolean;
  readonly finalArtifactSha256?: string | null;
  readonly policySnapshotVersion?: number | null;
  readonly currentPolicyVersion?: number | null;
  /** Exact package identity returned by the server, required for publishing QC. */
  readonly compilerPackageId?: string | null;
  readonly qcRecords?: readonly QcRecordWire[];
  readonly evidence?: unknown;
}

/** Pre-upload intake approval. Binds to the SELECTED DOCUMENT's SOURCE bytes. */
export interface IntakeBody {
  readonly documentId: string;
  /** The SOURCE hash the reviewer confirms (never a final-package hash). */
  readonly expectedArtifactSha256: string;
  readonly kind: 'attestation' | 'verified-record';
  readonly reason: string;
  readonly evidenceRef?: string;
}

/** Pre-release publishing-QC sign-off. Binds to the sealed FINAL package bytes
 *  AND the exact compiler package id the reviewer inspected. */
export interface QcBody {
  /** The FINAL (sealed package ZIP) hash the reviewer confirms. */
  readonly expectedArtifactSha256: string;
  /** The exact compiler package id the reviewer inspected (server-supplied). */
  readonly expectedCompilerPackageId: string;
  readonly reason: string;
  readonly evidenceRef?: string;
  readonly signedArtifactSha256?: string;
}

export interface QcRecordResponse {
  readonly ok: true;
  readonly record: QcRecordWire;
}

function base(submissionId: string): string {
  return `/api/v1/submissions/${encodeURIComponent(submissionId)}/navigation-release`;
}

export const navigationRelease = {
  get: (submissionId: string): Promise<NavigationReleaseStatus> => apiFetch(base(submissionId)),

  intake: (submissionId: string, body: IntakeBody): Promise<QcRecordResponse> =>
    apiFetch(`${base(submissionId)}/intake`, { method: 'POST', json: body }),

  qc: (submissionId: string, body: QcBody): Promise<QcRecordResponse> =>
    apiFetch(`${base(submissionId)}/qc`, { method: 'POST', json: body }),
};
